<template>
  <div class="d-flex flex-column mt-4">
    <div class="grid-header">
      <div class="d-flex align-items-center">
        <h1>{{ getTituloLectura() }}</h1>
      </div>
      <div class="toolbar">
        <button type="button" @click.prevent="anadirLectura()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>
    <DxDataGrid v-bind="{ ...getDefaultGridProperties(), paging: { pageSize: 5 } }" :data-source="gridSource"
      ref="grid">
      <DxColumnFixing :enabled="true" />

      <DxColumn v-if="props.tipoSuministro === TipoSuministroInmuebleEnum.Luz" data-field="Lectura"
        :caption="$t('inmueble.lectura.lecturaPunta')" data-type="number" cell-template="lecturaTemplate">
      </DxColumn>
      <DxColumn v-if="props.tipoSuministro === TipoSuministroInmuebleEnum.Luz" data-field="Lectura2"
        :caption="$t('inmueble.lectura.lecturaLlano')" data-type="number" cell-template="lecturaTemplate">
      </DxColumn>
      <DxColumn v-if="props.tipoSuministro === TipoSuministroInmuebleEnum.Luz" data-field="Lectura3"
        :caption="$t('inmueble.lectura.lecturaValle')" data-type="number" cell-template="lecturaTemplate">
      </DxColumn>
      <DxColumn v-if="props.tipoSuministro !== TipoSuministroInmuebleEnum.Luz" data-field="Lectura"
        :caption="$t('inmueble.lectura.lectura')" data-type="number" cell-template="lecturaTemplate">
      </DxColumn>
      <DxColumn data-field="Fecha" :caption="$t('inmueble.lectura.fecha')" data-type="date" format="dd/MM/yyyy">
      </DxColumn>

      <DxColumn type="buttons" :caption="$t('inmueble.lectura.ui.acciones')" :fixed="true"
        fixed-position="right" width="200px" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-eye text-success',
            hint: $t('inmueble.lectura.ui.ver'),
            onClick: (e: any) => verAdjuntos(e.row.data.Adjuntos),
            visible: (options: any) => options.row?.data.Adjuntos.length > 0
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-trash text-success',
            hint: $t('inmueble.lectura.ui.borrar'),
            onClick: (e: any) => borrar(e.row.data.Id)
          }
        ]"/>

      <template #lecturaTemplate="{ data }">
        <span v-if="data.value">
          {{ $n(data.value) }}
          {{ props.tipoSuministro === TipoSuministroInmuebleEnum.Agua ? 'm³' : 'kWh' }}
        </span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, type PropType } from 'vue';
import { DxDataGrid, DxColumnFixing, DxColumn } from 'devextreme-vue/data-grid';
import { useApi } from '@api/integration';
import { useIntl } from '@/.shared/services/i18n';
import { getDefaultGridProperties } from '@helpers/grid-helper';
import type CustomStore from 'devextreme/data/custom_store';
import { TipoSuministroInmuebleEnum, type DXLoadOptions } from '@/.shared/.api';
import { useModalService } from '@/.shared/services/modal-service';
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { AddLecturaInmueble } from '@/components/inmuebles/models/AddLecturaInmueble';
import AnadirLecturaForm from '@/components/inmuebles/AnadirLecturaForm.vue';
import type { IDocumentInitialState } from '@/.shared/components/document-card/models/IDocumentCardProps';

const api = useApi();
const intl = useIntl();

const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();

const props = defineProps({
  inmuebleId: {
    type: String,
    required: true
  },
  tipoSuministro: {
    type: String as PropType<TipoSuministroInmuebleEnum>,
    required: true
  }
});

onMounted(async () => {
  await setDataSource();
});

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getLecturasInmuebleForGrid", { showLoading: false }, props.inmuebleId, props.tipoSuministro, q)
    }
  });
}

const anadirLectura = () => {
  let title = `${intl.i18n.t("inmueble.lectura.ui.titulo_alta")}`;
  modal.showModal(
    AnadirLecturaForm,
    {
      model: new AddLecturaInmueble({ TipoSuministro: props.tipoSuministro }),
      inmuebleId: props.inmuebleId, tipoSuministro: props.tipoSuministro
    },
    { title: title, width: 600, height: 450 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const verAdjuntos = async (adjuntos: any[]) => {

  let documentList: IDocumentInitialState[] = [];
  for (const adjunto of adjuntos) {
    documentList.push({
      id: adjunto.Id,
      uri: adjunto.Ruta,
      fileName: adjunto.Nombre
    });
  }

  await modal.viewerDocumentBox({ documentList: documentList, align: 'horizontal', title: '' }, intl.i18n.t('inmueble.lectura.ui.titulo_adjuntos'));
}

const borrar = async (id: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t('inmueble.lectura.ui.borrar_pregunta'),
    intl.i18n.t('inmueble.lectura.ui.titulo_confirma_borrar'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteLecturaInmueble", { showLoading: true }, props.inmuebleId, id)
      .then(() => {
        toastSuccess(intl.i18n.t('inmueble.lectura.ui.borrado_exito'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('inmueble.lectura.ui.borrado_fallo')));
}

const getTituloLectura = () => {
  switch (props.tipoSuministro) {
    case TipoSuministroInmuebleEnum.Luz:
      return intl.i18n.t('inmueble.lectura.ui.titulo_lecturas_luz');
    case TipoSuministroInmuebleEnum.Gas:
      return intl.i18n.t('inmueble.lectura.ui.titulo_lecturas_gas');
    case TipoSuministroInmuebleEnum.Agua:
      return intl.i18n.t('inmueble.lectura.ui.titulo_lecturas_agua');
    default:
      return intl.i18n.t('inmueble.lectura.ui.titulo_lecturas');
  }
};

</script>